import { useState, useEffect } from 'react';
import {
  Group,
  Text,
  Stack,
  Box,
  ThemeIcon,
  Center,
  Loader,
  Collapse,
  ActionIcon,
  Avatar,
} from '@mantine/core';
import {
  Calendar,
  Clock,
  ChevronDown,
  ChevronRight,
} from 'react-feather';
import {
  coachPortalApi,
  type CoachCohortEvent,
} from '../../../services/api/coachPortal';
import { formatDateTime } from '../../../utils/formatters';
import { JoinVideoCall } from '../../../components/session/JoinVideoCall';
import { toMeetingHref } from '../../../utils/meetingLink';
import { cohortEventIcons } from './helpers';

export interface CoachCohortEventsSectionProps {
  cohortId: string;
}

export function CoachCohortEventsSection({ cohortId }: CoachCohortEventsSectionProps) {
  const [events, setEvents] = useState<CoachCohortEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await coachPortalApi.getCohortEvents(cohortId);
        setEvents(data);
      } catch {
        // Silently handle - section won't show
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [cohortId]);

  if (loading) {
    return (
      <Center py="md">
        <Loader size="sm" />
      </Center>
    );
  }

  if (events.length === 0) return null;

  const completedCount = events.filter((e) => e.status === 'completed').length;
  const upcomingCount = events.length - completedCount;

  return (
    <Box
      style={{
        border: '1px solid #E5E2DC',
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: '#FFFFFF',
      }}
    >
      {/* Header — clickable accordion */}
      <Group
        justify="space-between"
        onClick={() => setExpanded((v) => !v)}
        style={{
          cursor: 'pointer',
          padding: '16px 20px',
          borderBottom: expanded ? '1px solid #E5E2DC' : 'none',
        }}
        wrap="nowrap"
      >
        <Group gap="sm" wrap="nowrap">
          <Avatar size={40} radius="xl" color="gray" variant="light">
            <Calendar size={20} />
          </Avatar>
          <div>
            <Text fw={600} size="sm">Cohort Events</Text>
            <Text size="13px" c="dimmed">
              {completedCount} completed · {upcomingCount} upcoming
            </Text>
          </div>
        </Group>
        <ActionIcon variant="subtle" color="gray" size="sm">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </ActionIcon>
      </Group>

      {/* Event rows */}
      <Collapse in={expanded}>
        <Stack gap={0}>
          {events.map((event) => {
            const Icon = cohortEventIcons[event.event_type] || Calendar;
            return (
              <Group
                key={event.id}
                justify="space-between"
                style={{
                  padding: '12px 20px',
                  borderBottom: '1px solid var(--mantine-color-gray-2)',
                }}
                wrap="nowrap"
              >
                <Group gap="sm" wrap="nowrap">
                  <ThemeIcon size={32} radius="xl" variant="light" color="gray">
                    <Icon size={14} />
                  </ThemeIcon>
                  <div>
                    <Text fw={500} size="sm">
                      {event.event_name}
                    </Text>
                    {event.scheduled_start ? (
                      <Group gap="xs">
                        <Clock size={11} color="var(--mantine-color-gray-6)" />
                        <Text size="xs" c="dimmed">
                          {formatDateTime(event.scheduled_start)}
                        </Text>
                      </Group>
                    ) : (
                      <Text size="xs" c="dimmed">
                        Date TBD
                      </Text>
                    )}
                  </div>
                </Group>

                <Group gap="sm">
                  {/* MED-1349: the meeting link opens in a new tab; it was a
                      label that looked like a link. toMeetingHref adds
                      https:// and drops any other scheme. */}
                  <JoinVideoCall href={toMeetingHref(event.video_link)} variant="link" />
                </Group>
              </Group>
            );
          })}
        </Stack>
      </Collapse>
    </Box>
  );
}
